import React, { useState, useEffect, useRef } from 'react';
import { Card, Image, Text, Group, Badge } from '@mantine/core';
import { Carousel } from '@mantine/carousel';
import Autoplay from 'embla-carousel-autoplay';
import { SectionTitle } from '../common/SectionTitle';
import { galleryService } from '../../services/galleryService';
import { Loader } from '../../components/common/Loader';
const API_URL =
  import.meta.env.VITE_API_BASE_URL ||
  'https://thalvora-exports.onrender.com';

interface Certification {
  id: string;
  name: string;
  description: string;
  image: string;
  issuer: string;
  issueDate: string;
  expiryDate: string;
  order: number;
}

export const Certifications: React.FC = () => {
  const [certifications, setCertifications] = useState<Certification[]>([]);
  const [loading, setLoading] = useState(true);
  const autoplay = useRef(
    Autoplay({ delay: 4000, stopOnInteraction: false })
  );

  useEffect(() => {
    const loadData = async () => {
      try {
        // 🔥 STEP 1: Cache check
        const cached = sessionStorage.getItem('certifications');

        if (cached) {
          setCertifications(JSON.parse(cached));
          setLoading(false);
          return;
        }

        // 🔥 STEP 2: API call
        const gallery = await galleryService.getPublicGallery(
          'CERTIFICATIONS'
        );

        const items = gallery.items
          .filter((item) => item.type === 'IMAGE' && item.active)
          .map((item) => {
            let meta = {};
            try {
              meta = JSON.parse(item.metadata || '{}');
            } catch (e) {}

            return {
              id: item.id,
              name: item.title,
              description: item.description,
              image: item.imageUrl,
              issuer: (meta as any).issuer || '',
              issueDate: (meta as any).issueDate || '',
              expiryDate: (meta as any).expiryDate || '',
              order: (meta as any).order || 0,
            };
          })
          .sort((a, b) => a.order - b.order);

        setCertifications(items);

        // 🔥 STEP 3: Save cache
        sessionStorage.setItem(
          'certifications',
          JSON.stringify(items)
        );
      } catch (error) {
        console.error('Error loading certifications:', error);
        setCertifications([]);
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, []);

  const getImageUrl = (cert: Certification) => {
    if (!cert.image) return '';
    if (
      cert.image.startsWith('http') ||
      cert.image.startsWith('/assets')
    )
      return cert.image;

    return `${API_URL}${cert.image.startsWith('/') ? '' : '/'}${
      cert.image
    }`;
  };

  return (
    <div>
      <SectionTitle
        title="Our Certifications"
        subtitle="Recognized standards and quality assurances"
      />

      {/* 🔥 Non-blocking loader */}
      {loading && (
        <div className="flex justify-center py-6">
          <Loader/>
        </div>
      )}

      {!loading && certifications.length === 0 && (
        <Text ta="center" mt="lg">
          No certifications available
        </Text>
      )}

      {!loading && certifications.length > 0 && (
        <Carousel
          slideSize={{
            base: '100%',
            sm: '50%',
            md: '33.33%',
            lg: '25%',
          }}
          slideGap="md"
          align="start"
          withIndicators={certifications.length > 3}
          loop
          containScroll="keepSnaps"
          controlSize={28}
          plugins={[autoplay.current]}
          onMouseEnter={autoplay.current.stop}
          onMouseLeave={autoplay.current.reset}
        >
          {certifications.map((cert) => {
            const finalImage =
              getImageUrl(cert) ||
              "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='400' height='300'%3E%3Crect width='100%25' height='100%25' fill='%23f0f0f0'/%3E%3Ctext x='50%25' y='50%25' text-anchor='middle' fill='%23999'%3ENo Image%3C/text%3E%3C/svg%3E";

            return (
              <Carousel.Slide key={cert.id}>
                <Card
                  padding="lg"
                  radius="lg"
                  withBorder
                  className="hover:transform hover:-translate-y-1 hover:shadow-lg transition-all duration-200"
                  style={{ height: '100%' }}
                >
                  <Card.Section>
                    <Image
                      src={finalImage}
                      height={160}
                      alt={cert.name}
                      fit="cover"
                      loading="lazy"
                      decoding="async"
                    />
                  </Card.Section>

                  <Group justify="space-between" mt="md" mb="xs">
                    <Text fw={600} size="lg">
                      {cert.name}
                    </Text>
                    {cert.issuer && (
                      <Badge color="exportGreen" variant="light">
                        {cert.issuer}
                      </Badge>
                    )}
                  </Group>

                  <Text size="sm" c="dimmed" lineClamp={2}>
                    {cert.description || 'Certification credential'}
                  </Text>
                </Card>
              </Carousel.Slide>
            );
          })}
        </Carousel>
      )}
    </div>
  );
};